import { APP_CONTEXT } from '@/utils/context';
import { checkPermissions } from '@/utils/utils';
import PropTypes from 'prop-types';
import { useContext } from 'react';

/**
 * Permission Wrapper component.
 * The intent of this is to ensure that the item that is being rendered should be rendered
 * @param {object} props The component props.
 * @param {JSX.Element} props.children The children to render.
 * @param {string[]} props.permissions The permissions to check.
 * @param {boolean} props.satisfyAll If all permissions should be satisfied.
 * @param {string} props.altText The alternative text to render if the permissions are not satisfied.
 * @param {boolean} props.negativeCheck If the check should be negative.
 * @param {JSX.Element} props.fallback The fallback component to render if the permissions are not satisfied.
 * @returns {JSX.Element | string} The rendered Permission Wrapper component.
 */
const PermissionWrapper = ({
  children,
  permissions,
  satisfyAll,
  altText,
  negativeCheck,
  fallback,
}) => {
  const { permissions: userPermissions = [], isSuper } =
    useContext(APP_CONTEXT);

  if (negativeCheck) {
    if (
      !isSuper &&
      permissions &&
      checkPermissions(userPermissions, permissions, satisfyAll)
    ) {
      if (altText) {
        return altText;
      }
      if (fallback) {
        return fallback;
      }
      return null;
    }
    return children;
  }

  if (
    isSuper ||
    (permissions && checkPermissions(userPermissions, permissions, satisfyAll))
  ) {
    return children;
  }
  if (altText) {
    return altText;
  }
  if (fallback) {
    return fallback;
  }

  return null;
};

PermissionWrapper.defaultProps = {
  permissions: undefined,
  satisfyAll: true,
  altText: undefined,
  fallback: undefined,
  negativeCheck: false,
};

PermissionWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  fallback: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  permissions: PropTypes.arrayOf(PropTypes.string),
  satisfyAll: PropTypes.bool,
  altText: PropTypes.string,
  negativeCheck: PropTypes.bool,
};

export default PermissionWrapper;
